import { XDMWrapper, TARGETS } from '@gt-common/event-logger-web';
import { RailsLandingPageSchema } from './railsSchemas';
import { generateUUID, isXdmTrackingEnabled, buildSearchContext, RAILS_CONSTANTS, getAdvancePurchaseDays } from './xdmcommonutils';
import { getLocusV2Data } from '../pages/listing/listingStore/ListingActions';

const eventTrackingContext = () => ({
  organisation: RAILS_CONSTANTS.ORGANISATION,
  bu: RAILS_CONSTANTS.BU,
  env: process.env.NODE_ENV === 'development' ? 'development' : 'production',
  template_id: RAILS_CONSTANTS.TEMPLATE_ID,
  topic_name: RAILS_CONSTANTS.TOPIC_NAME,
  timezone: RAILS_CONSTANTS.TIMEZONE,
  funnel_source: RAILS_CONSTANTS.FUNNEL_SOURCE
});

const EVENT_NAMES = {
  TRAVELLER_LOAD: 'TRAVELLER_LOAD',
  BOARDING_POINT_CHANGE: 'BOARDING_POINT_CHANGE',
  FC_OPTED: 'FC_OPTED',
  CHANGE_IRCTC_USERNAME: 'CHANGE_IRCTC_USERNAME',
  GET_NEW_PASSWORD: 'GET_NEW_PASSWORD',
  WHY_IS_THIS_REQUIRED: 'WHY_IS_THIS_REQUIRED',
  APPLY_COUPON: 'APPLY_COUPON',
  REMOVE_COUPON: 'REMOVE_COUPON',
  ADDITIONAL_PREFERENCE_EXPAND: 'ADDITIONAL_PREFERENCE_EXPAND',
  ADDITIONAL_PREFERENCE_VIEW_MORE: 'ADDITIONAL_PREFERENCE_VIEW_MORE',
  ADDITIONAL_PREFERENCE_VIEW_ALL: 'ADDITIONAL_PREFERENCE_VIEW_ALL',
  PAY_AND_BOOK: 'PAY_AND_BOOK',
};

const EVENT_VALUES = {
  FUNNEL_RAILS_TRAVELLERS: 'funnel:rails:travellers',
  REVIEW_CLICKED: 'review_clicked',
  BOARDING_STATION_CLICKED: 'boarding_station_clicked',
  FREE_CANCELLATION_OPTED: 'free_cancellation_opted',
  FREE_CANCELLATION_DESELECTED: 'free_cancellation_deselected',
  TRAVELLER_COUPON_APPLIED: 'traveller_coupon_applied',
  COUPON_REMOVED: 'coupon_removed',
  IRCTC_USERNAME_CHANGE: 'irctc_username_change',
  GET_NEW_IRCTC_PASSWORD_CLICKED: 'get_new_irctc_password_clicked',
  WHY_IS_THIS_REQUIRED: 'why_is_this_required',
  ADDITIONAL_PREFERENCE_EXPAND: 'additional_preference_expand',
  ADDITIONAL_PREFERENCE_VIEW_MORE: 'additional_preference_view_more',
  ADDITIONAL_PREFERENCE_VIEW_ALL: 'additional_preference_view_all',
};

const LOB_NAME = 'rails';

const getStationData = async (fromStationNode, toStationNode, railInfo) => {
  const urlParams = new URLSearchParams(window.location.search);
  const srcCode = fromStationNode?.code || urlParams.get('from') || railInfo?.train?.frmStnCode || '';
  const destCode = toStationNode?.code || urlParams.get('to') || railInfo?.train?.toStnCode || '';
  const srcCity = fromStationNode?.city || railInfo?.train?.frmStnName || srcCode;
  const destCity = toStationNode?.city || railInfo?.train?.toStnName || destCode;
  
  const locus = (!fromStationNode?.locusV2Id && srcCode && destCode) 
    ? await getLocusV2Data(srcCode, destCode).catch(() => null) 
    : null;
  
  const fromStation = {
    code: srcCode, city: srcCity,
    locusCode: fromStationNode?.locusCode || locus?.fromLocusCode || srcCode,
    locusType: fromStationNode?.locusType || locus?.sourceLocusType || '',
    locusV2Id: fromStationNode?.locusV2Id || locus?.sourceLocusV2Id || '',
    locusV2Type: fromStationNode?.locusV2Type || locus?.sourceLocusV2Type || '',
  };
  
  const toStation = {
    code: destCode, city: destCity,
    locusCode: toStationNode?.locusCode || locus?.toLocusCode || destCode,
    locusType: toStationNode?.locusType || locus?.destinationLocusType || '',
    locusV2Id: toStationNode?.locusV2Id || locus?.destinationLocusV2Id || '',
    locusV2Type: toStationNode?.locusV2Type || locus?.destinationLocusV2Type || '',
  };
  
  return { fromStation, toStation, srcCode, destCode, srcCity, destCity };
};

const sendXDMEvent = async (payload, eventName, isXdmEnabled = false) => {
  if (!isXdmTrackingEnabled(isXdmEnabled)) {
    return;
  }

  await XDMWrapper.logEvent({
    payload,
    schema: RailsLandingPageSchema,
    otherConfig: {
      lobName: LOB_NAME,
      adobeKeyConfig: []
    },
    target: TARGETS.ALL,
    extraOptions: {
      lob: LOB_NAME,
      destinationUrl: typeof window !== 'undefined' ? window.location.href : ''
    }
  });
};

export const trackTravellerPageLoad = async ({
  railInfo,
  freeCancellationResponse,
  confirmationGuaranteeResponseNew,
  freeCancellationSelectedOption,
  railofySelectedOption,
  fromStationNode,
  toStationNode,
  isXdmEnabled = false,
}) => {
  try {
    const { fromStation, toStation, srcCode: srcStn, destCode: destStn, srcCity, destCity } = await getStationData(fromStationNode, toStationNode, railInfo);
    const addonDetails = [];

    if (freeCancellationResponse) {
      const fcResponse = freeCancellationResponse;
      const fcOption = fcResponse?.cancellationOptions?.[0];
      const fcPremiumDetails = fcResponse?.freeCancellationPremiumDetails;
      const isAvailable = !!(fcResponse?.cancellationOptions && fcResponse.cancellationOptions.length > 0);

      if (isAvailable) {
        const refundAmountStr = fcOption?.refundAmount || '';
        const refundAmountMatch = refundAmountStr.match(/\d+/);
        const refundAmount = refundAmountMatch ? parseInt(refundAmountMatch[0], 10) : 0;
        
        addonDetails.push({
          addontype: 'FC',
          id: 'FC',
          currency: 'INR',
          includedunits: 1,
          unitprice: fcOption?.insuranceAmount || fcPremiumDetails?.insurancePremium || 0,
          unittype: 'INR',
          heading: 'FC',
          selected: !!freeCancellationSelectedOption,
          available: true,
          is_preselected: false,
          refund_amount: refundAmount,
          premium_percentage: fcPremiumDetails?.premiumPercentage || 0
        });
      }
    }

    if (confirmationGuaranteeResponseNew) {
      const tgResponse = confirmationGuaranteeResponseNew;
      const tgOption = tgResponse?.preferenceInfoRevamped?.[0];
      const isAvailable = !!(tgResponse?.preferenceInfoRevamped && tgResponse.preferenceInfoRevamped.length > 0);

      if (isAvailable) {
        const fareInfo = tgResponse?.fareInfoRevamped?.[1]; 
        const refundValueStr = fareInfo?.value?.[0]?.text || '';
        const refundMatch = refundValueStr.match(/\d+/);
        const refundAmount = refundMatch ? parseInt(refundMatch[0], 10) : 0;
        
        addonDetails.push({
          addontype: 'TG',
          id: 'TG',
          currency: 'INR',
          includedunits: 1,
          unitprice: tgResponse?.tgPremAmt || tgOption?.premiumPerPerson || 0,
          unittype: 'INR',
          heading: 'TG',
          selected: !!railofySelectedOption,
          available: true,
          is_preselected: false,
          refund_amount: refundAmount,
          premium_percentage: tgResponse?.premiumPercentage || 0
        });
      }
    }

    const xdmPayload = {
      page_context: {
        page_type: 'funnel',
        page_name: 'review',
        funnel_step: 'review',
        lob: 'rails',
        lob_category: 'rails',
        travel_store: {
          store_id: 'MMT'
        }
      },
      search_context: {
        ...buildSearchContext(fromStation, toStation, 'traveller'),
        advance_purchase: getAdvancePurchaseDays(railInfo?.doj)
      },
      event_detail: {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: 'page-rendered',
        event_type: 'page',
        event_value: EVENT_VALUES.FUNNEL_RAILS_TRAVELLERS,
        components: {
          id: 'traveller_page_component',
          product_list: [
            {
              id: `${railInfo?.train?.trainNumber}_${srcStn}_${destStn}_${railInfo?.doj}`,
              rank: 0,
              legs: [
                {
                  id: `${railInfo?.train?.trainNumber}_${srcStn}_${destStn}_${railInfo?.doj}`,
                  name: railInfo?.train?.trainName || '',
                  number: railInfo?.train?.trainNumber || '',
                  product_type: 'O',
                  duration: railInfo?.train?.duration || 0,
                  category: 'rails',
                  sku_type: 'Regular Train',
                  is_cluster: false,
                  is_dynamic_pricing_applicable: false,
                  availability_shown: railInfo?.availability?.availablityStatus || '',
                  fare_shown: railInfo?.availability?.totalFare || 0,
                  from_station: {
                    code: railInfo?.train?.frmStnCode || srcStn,
                    name: railInfo?.train?.frmStnName || srcCity,
                    departure_time: railInfo?.train?.departureTime || '',
                    departure_date: railInfo?.doj || ''
                  },
                  to_station: {
                    code: railInfo?.train?.toStnCode || destStn,
                    name: railInfo?.train?.toStnName || destCity,
                    arrival_time: railInfo?.train?.arrivalTime || '',
                    arrival_date: railInfo?.doj || ''
                  },
                  boarding_station: {
                    code: (typeof railInfo?.train?.boardingStation === 'string' ? railInfo?.train?.boardingStation : null) || railInfo?.train?.frmStnCode || srcStn || '',
                    name: railInfo?.train?.frmStnName || srcCity || '',
                    departure_time: railInfo?.train?.departureTime || '',
                    departure_date: railInfo?.doj || ''
                  },
                  catalog_list: [
                    {
                      quota: railInfo?.selectedQuota || '',
                      class_name: railInfo?.selectedClass || '',
                      price: {
                        total_amount: railInfo?.availability?.totalFare || 0
                      },
                      status: railInfo?.availability?.availablityStatus || '',
                      date: railInfo?.availability?.availablityDate || railInfo?.doj || '',
                      availability_last_updated: railInfo?.availability?.lastUpdatedOn || '',
                      free_cancellation_available: !!railInfo?.availability?.freeCancellationText,
                      trip_gaurantee_available: !!railInfo?.availability?.confirmationGuaranteeText,
                      source_of_data: 'landing'
                    }
                  ]
                }
              ]
            }
          ],
          time_spent: 0
        }
      },
      addon_details: addonDetails,
      event_tracking_context: eventTrackingContext()
    };

    await sendXDMEvent(xdmPayload, EVENT_NAMES.TRAVELLER_LOAD, isXdmEnabled);
  } catch (xdmError) {
    console.error(`${EVENT_NAMES.TRAVELLER_LOAD} Error:`, xdmError.message);
  }
};

export const trackBoardingPointChangeClicked = async ({
  railInfo,
  boardingStation,
  fromStationNode,
  toStationNode,
  isXdmEnabled = false,
}) => {
  try {
    const { fromStation, toStation } = await getStationData(fromStationNode, toStationNode, railInfo);

    const xdmPayload = {
      page_context: {
        page_type: 'funnel',
        page_name: 'review',
        funnel_step: 'review',
        lob: 'rails',
        lob_category: 'rails',
        travel_store: {
          store_id: 'MMT'
        }
      },
      search_context: {
        ...buildSearchContext(fromStation, toStation, 'traveller'),
        advance_purchase: getAdvancePurchaseDays(railInfo?.doj)
      },
      event_detail: {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: 'button-clicked',
        event_type: 'user_interaction',
        event_value: EVENT_VALUES.BOARDING_STATION_CLICKED,
        components: {
          id: 'boarding_point_change',
          boarding_station: {
            code: boardingStation?.stationCode || '',
            name: boardingStation?.stationName || '',
            departure_time: boardingStation?.departureTime || '',
            day_count: boardingStation?.dayCount || 0
          }
        }
      },
      event_tracking_context: eventTrackingContext()
    };

    await sendXDMEvent(xdmPayload, EVENT_NAMES.BOARDING_POINT_CHANGE, isXdmEnabled);
  } catch (xdmError) {
    console.error(`${EVENT_NAMES.BOARDING_POINT_CHANGE} Error:`, xdmError.message);
  }
};

export const trackFCOpted = async ({
  railInfo,
  freeCancellationResponse,
  isSelected,
  fromStationNode,
  toStationNode,
  isXdmEnabled = false,
}) => {
  
  try {
    const { fromStation, toStation } = await getStationData(fromStationNode, toStationNode, railInfo);

    const fcOption = freeCancellationResponse?.cancellationOptions?.[0];
    const fcPremiumDetails = freeCancellationResponse?.freeCancellationPremiumDetails;

    const refundAmountStr = fcOption?.refundAmount || '';
    const refundAmountMatch = refundAmountStr.match(/\d+/);
    const refundAmount = refundAmountMatch ? parseInt(refundAmountMatch[0], 10) : 0;

    const xdmPayload = {
      page_context: {
        page_type: 'funnel',
        page_name: 'review',
        funnel_step: 'review',
        lob: 'rails',
        lob_category: 'rails',
        travel_store: {
          store_id: 'MMT'
        }
      },
      search_context: {
        ...buildSearchContext(fromStation, toStation, 'traveller'),
        advance_purchase: getAdvancePurchaseDays(railInfo?.doj)
      },
      event_detail: {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: 'button-clicked',
        event_type: 'user_interaction',
        event_value: isSelected ? EVENT_VALUES.FREE_CANCELLATION_OPTED : EVENT_VALUES.FREE_CANCELLATION_DESELECTED,
        components: {
          id: 'free_cancellation_selection'
        }
      },
      addon_details: [
        {
          addontype: 'FC',
          id: 'FC',
          currency: 'INR',
          includedunits: 1,
          unitprice: fcOption?.insuranceAmount || fcPremiumDetails?.insurancePremium || 0,
          unittype: 'INR',
          heading: 'FC',
          selected: isSelected,
          available: true,
          refund_amount: refundAmount,
          premium_percentage: fcPremiumDetails?.premiumPercentage || 0
        }
      ],
      event_tracking_context: eventTrackingContext()
    };
    
    await sendXDMEvent(xdmPayload, EVENT_NAMES.FC_OPTED, isXdmEnabled);
  } catch (xdmError) {
    console.error(`${EVENT_NAMES.FC_OPTED} Error:`, xdmError.message);
  }
};

export const trackChangeIRCTCUsernameClicked = async ({
  railInfo,
  fromStationNode,
  toStationNode,
  isXdmEnabled = false,
}) => {

  try {
    const { fromStation, toStation } = await getStationData(fromStationNode, toStationNode, railInfo);

    const xdmPayload = {
      page_context: {
        page_type: 'funnel',
        page_name: 'review',
        funnel_step: 'review',
        lob: 'rails',
        lob_category: 'rails',
        travel_store: {
          store_id: 'MMT'
        }
      },
      search_context: {
        ...buildSearchContext(fromStation, toStation, 'traveller'),
        advance_purchase: getAdvancePurchaseDays(railInfo?.doj)
      },
      event_detail: {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: 'button-clicked',
        event_type: 'user_interaction',
        event_value: EVENT_VALUES.IRCTC_USERNAME_CHANGE,
        components: {
          id: 'change_irctc_username'
        }
      },
      event_tracking_context: eventTrackingContext()
    };

    await sendXDMEvent(xdmPayload, EVENT_NAMES.CHANGE_IRCTC_USERNAME, isXdmEnabled);
  } catch (xdmError) {
    console.error(`${EVENT_NAMES.CHANGE_IRCTC_USERNAME} Error:`, xdmError.message);
  }
};

export const trackGetNewPasswordClicked = async ({
  railInfo,
  fromStationNode,
  toStationNode,
  isXdmEnabled = false,
}) => {
  try {
    const { fromStation, toStation } = await getStationData(fromStationNode, toStationNode, railInfo);

    const xdmPayload = {
      page_context: {
        page_type: 'funnel',
        page_name: 'review',
        funnel_step: 'review',
        lob: 'rails',
        lob_category: 'rails',
        travel_store: {
          store_id: 'MMT'
        }
      },
      search_context: {
        ...buildSearchContext(fromStation, toStation, 'traveller'),
        advance_purchase: getAdvancePurchaseDays(railInfo?.doj)
      },
      event_detail: {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: 'button-clicked',
        event_type: 'user_interaction',
        event_value: EVENT_VALUES.GET_NEW_IRCTC_PASSWORD_CLICKED,
        components: {
          id: 'get_new_password'
        }
      },
      event_tracking_context: eventTrackingContext()
    };
    await sendXDMEvent(xdmPayload, EVENT_NAMES.GET_NEW_PASSWORD, isXdmEnabled);
  } catch (xdmError) {
    console.error(`${EVENT_NAMES.GET_NEW_PASSWORD} Error:`, xdmError.message);
  }
};

export const trackWhyIsThisRequiredClicked = async ({
  railInfo,
  fromStationNode,
  toStationNode,
  isXdmEnabled = false,
}) => {
  try {
    const { fromStation, toStation } = await getStationData(fromStationNode, toStationNode, railInfo);

    const xdmPayload = {
      page_context: {
        page_type: 'funnel',
        page_name: 'review',
        funnel_step: 'review',
        lob: 'rails',
        lob_category: 'rails',
        travel_store: {
          store_id: 'MMT'
        }
      },
      search_context: {
        ...buildSearchContext(fromStation, toStation, 'traveller'),
        advance_purchase: getAdvancePurchaseDays(railInfo?.doj)
      },
      event_detail: {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: 'button-clicked',
        event_type: 'user_interaction',
        event_value: EVENT_VALUES.WHY_IS_THIS_REQUIRED,
        components: {
          id: 'why_is_this_required'
        }
      },
      event_tracking_context: eventTrackingContext()
    };
    await sendXDMEvent(xdmPayload, EVENT_NAMES.WHY_IS_THIS_REQUIRED, isXdmEnabled);
  } catch (xdmError) {
    console.error(`${EVENT_NAMES.WHY_IS_THIS_REQUIRED} Error:`, xdmError.message);
  }
};

export const trackApplyCouponClicked = async ({
  railInfo,
  couponCode,
  couponDetails,
  fromStationNode,
  toStationNode,
  isXdmEnabled = false,
}) => {
  try {
    const { fromStation, toStation } = await getStationData(fromStationNode, toStationNode, railInfo);

    const discountAmount = couponDetails?.discountAmount 
      || couponDetails?.discount 
      || couponDetails?.couponDiscount 
      || couponDetails?.discountValue 
      || couponDetails?.amount 
      || 0;
    const couponDescription = couponDetails?.description 
      || couponDetails?.couponDescription 
      || couponDetails?.desc 
      || couponDetails?.message 
      || '';
    const couponHeading = couponDetails?.heading 
      || couponDetails?.title 
      || couponDetails?.couponTitle 
      || couponDetails?.displayName 
      || couponCode;

    const xdmPayload = {
      page_context: {
        page_type: 'funnel',
        page_name: 'review',
        funnel_step: 'review',
        lob: 'rails',
        lob_category: 'rails',
        travel_store: {
          store_id: 'MMT'
        }
      },
      search_context: {
        ...buildSearchContext(fromStation, toStation, 'traveller'),
        advance_purchase: getAdvancePurchaseDays(railInfo?.doj)
      },
      event_detail: {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: 'button-clicked',
        event_type: 'user_interaction',
        event_value: EVENT_VALUES.TRAVELLER_COUPON_APPLIED,
        components: {
          id: 'apply_coupon',
        }
      },
      addon_details: [
        {
          addontype: 'COUPON',
          id: couponCode,
          currency: 'INR',
          includedunits: 1,
          unitprice: Math.abs(discountAmount),
          unittype: 'INR',
          heading: couponHeading,
          selected: true,
          available: true,
          is_preselected: false,
          description: couponDescription
        }
      ],
      event_tracking_context: eventTrackingContext()
    };
    await sendXDMEvent(xdmPayload, EVENT_NAMES.APPLY_COUPON, isXdmEnabled);
  } catch (xdmError) {
    console.error(`${EVENT_NAMES.APPLY_COUPON} Error:`, xdmError.message);
  }
};

export const trackRemoveCouponClicked = async ({
  railInfo,
  couponCode,
  couponDetails,
  fromStationNode,
  toStationNode,
  isXdmEnabled = false,
}) => {
  try {
    const { fromStation, toStation } = await getStationData(fromStationNode, toStationNode, railInfo);
    const discountAmount = couponDetails?.discountAmount 
      || couponDetails?.discount 
      || couponDetails?.couponDiscount 
      || couponDetails?.discountValue 
      || couponDetails?.amount 
      || 0;
    const couponDescription = couponDetails?.description 
      || couponDetails?.couponDescription 
      || couponDetails?.desc 
      || couponDetails?.message 
      || '';
    const couponHeading = couponDetails?.heading 
      || couponDetails?.title 
      || couponDetails?.couponTitle 
      || couponDetails?.displayName 
      || couponCode;

    const xdmPayload = {
      page_context: {
        page_type: 'funnel',
        page_name: 'review',
        funnel_step: 'review',
        lob: 'rails',
        lob_category: 'rails',
        travel_store: {
          store_id: 'MMT'
        }
      },
      search_context: {
        ...buildSearchContext(fromStation, toStation, 'traveller'),
        advance_purchase: getAdvancePurchaseDays(railInfo?.doj)
      },
      event_detail: {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: 'button-clicked',
        event_type: 'user_interaction',
        event_value: EVENT_VALUES.COUPON_REMOVED,
        components: {
          id: 'remove_coupon'
        }
      },
      addon_details: [
        {
          addontype: 'COUPON',
          id: couponCode,
          currency: 'INR',
          includedunits: 1,
          unitprice: Math.abs(discountAmount),
          unittype: 'INR',
          heading: couponHeading,
          selected: false,
          available: true,
          is_preselected: false,
          description: couponDescription
        }
      ],
      event_tracking_context: eventTrackingContext()
    };
    await sendXDMEvent(xdmPayload, EVENT_NAMES.REMOVE_COUPON, isXdmEnabled);
  } catch (xdmError) {
    console.error(`${EVENT_NAMES.REMOVE_COUPON} Error:`, xdmError.message);
  }
};

export const trackAdditionalPreferenceExpand = async ({
  railInfo,
  fromStationNode,
  toStationNode,
  isXdmEnabled = false,
}) => {
  try {
    const { fromStation, toStation } = await getStationData(fromStationNode, toStationNode, railInfo);

    const xdmPayload = {
      page_context: {
        page_type: 'funnel',
        page_name: 'review',
        funnel_step: 'review',
        lob: 'rails',
        lob_category: 'rails',
        travel_store: {
          store_id: 'MMT'
        }
      },
      search_context: {
        ...buildSearchContext(fromStation, toStation, 'traveller'),
        advance_purchase: getAdvancePurchaseDays(railInfo?.doj)
      },
      event_detail: {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: 'button-clicked',
        event_type: 'user_interaction',
        event_value: EVENT_VALUES.ADDITIONAL_PREFERENCE_EXPAND,
        components: {
          id: 'additional_preference_expand'
        }
      },
      event_tracking_context: eventTrackingContext()
    };
    await sendXDMEvent(xdmPayload, EVENT_NAMES.ADDITIONAL_PREFERENCE_EXPAND, isXdmEnabled);
  } catch (xdmError) {
    console.error(`${EVENT_NAMES.ADDITIONAL_PREFERENCE_EXPAND} Error:`, xdmError.message);
  }
};

export const trackAdditionalPreferenceViewMoreClicked = async ({
  railInfo,
  fromStationNode,
  toStationNode,
  isXdmEnabled = false,
}) => {
  try {
    const { fromStation, toStation } = await getStationData(fromStationNode, toStationNode, railInfo);

    const xdmPayload = {
      page_context: {
        page_type: 'funnel',
        page_name: 'review',
        funnel_step: 'review',
        lob: 'rails',
        lob_category: 'rails',
        travel_store: {
          store_id: 'MMT'
        }
      },
      search_context: {
        ...buildSearchContext(fromStation, toStation, 'traveller'),
        advance_purchase: getAdvancePurchaseDays(railInfo?.doj)
      },
      event_detail: {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: 'button-clicked',
        event_type: 'user_interaction',
        event_value: EVENT_VALUES.ADDITIONAL_PREFERENCE_VIEW_MORE,
        components: {
          id: 'additional_preference_view_more'
        }
      },
      event_tracking_context: eventTrackingContext()
    };
    await sendXDMEvent(xdmPayload, EVENT_NAMES.ADDITIONAL_PREFERENCE_VIEW_MORE, isXdmEnabled);
  } catch (xdmError) {
    console.error(`${EVENT_NAMES.ADDITIONAL_PREFERENCE_VIEW_MORE} Error:`, xdmError.message);
  }
};
export const trackAdditionalPreferenceViewAllClicked = async ({
  railInfo,
  fromStationNode,
  toStationNode,
  isXdmEnabled = false,
}) => {
  try {
    const { fromStation, toStation } = await getStationData(fromStationNode, toStationNode, railInfo);

    const xdmPayload = {
      page_context: {
        page_type: 'funnel',
        page_name: 'review',
        funnel_step: 'review',
        lob: 'rails',
        lob_category: 'rails',
        travel_store: {
          store_id: 'MMT'
        }
      },
      search_context: {
        ...buildSearchContext(fromStation, toStation, 'traveller'),
        advance_purchase: getAdvancePurchaseDays(railInfo?.doj)
      },
      event_detail: {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: 'button-clicked',
        event_type: 'user_interaction',
        event_value: EVENT_VALUES.ADDITIONAL_PREFERENCE_VIEW_ALL,
        components: {
          id: 'additional_preference_view_all'
        }
      },
      event_tracking_context: eventTrackingContext()
    };
    await sendXDMEvent(xdmPayload, EVENT_NAMES.ADDITIONAL_PREFERENCE_VIEW_ALL, isXdmEnabled);
  } catch (xdmError) {
    console.error(`${EVENT_NAMES.ADDITIONAL_PREFERENCE_VIEW_ALL} Error:`, xdmError.message);
  }
};

export const trackPayAndBookClicked = async ({
  railInfo,
  selectedTravellers,
  selectedCoupon,
  freeCancellationResponse,
  confirmationGuaranteeResponseNew,
  freeCancellationSelectedOption,
  railofySelectedOption,
  fromStationNode,
  toStationNode,
  isXdmEnabled = false,
}) => {
  
  try {
    const { srcCode: srcStn, destCode: destStn, srcCity, destCity } = await getStationData(fromStationNode, toStationNode, railInfo);
    const doj = railInfo?.doj || '';

    const totalCollectibleAmount = railInfo?.availabilityBookingConfig?.totalCollectibleAmount || 0;
    const discountAmount = selectedCoupon?.discountAmount || selectedCoupon?.discount || 0;

    const departureTime = railInfo?.train?.departureTime || '';
    let fromDateTime = Date.now();
    if (doj && departureTime) {
      try {
        const year = doj.substring(0, 4);
        const month = doj.substring(4, 6);
        const day = doj.substring(6, 8);
        const [hours, minutes] = departureTime.split(':');
        const dateObj = new Date(`${year}-${month}-${day}T${hours}:${minutes}:00`);
        fromDateTime = dateObj.getTime();
      } catch (e) {
        console.error('Error parsing date:', e);
      }
    }
    const addonDetails = [];
    if (freeCancellationSelectedOption && freeCancellationResponse) {
      const fcAmount = freeCancellationResponse?.insuranceAmount || 0;
      const refundAmount = freeCancellationResponse?.refundAmount || 0;
      const premiumPercentage = freeCancellationResponse?.premiumPercentage || 0;
      
      if (fcAmount > 0) {
        addonDetails.push({
          addontype: 'FC',
          id: 'FC',
          currency: 'INR',
          includedunits: -1,
          unitprice: fcAmount,
          unittype: 'INR',
          heading: 'FC',
          selected: true,
          available: true,
          is_preselected: false,
          refund_amount: refundAmount,
          premium_percentage: premiumPercentage,
          discount: Math.abs(discountAmount),
          variant: 0
        });
      }
    }
    if (selectedCoupon && selectedCoupon.couponCode) {
      addonDetails.push({
        addontype: 'COUPON',
        id: selectedCoupon.couponCode,
        currency: 'INR',
        includedunits: 1,
        unitprice: Math.abs(discountAmount),
        unittype: 'INR',
        heading: selectedCoupon.couponCode,
        selected: true,
        available: true,
        is_preselected: false,
        discount: Math.abs(discountAmount),
        variant: 0
      });
    }
    const travellerInfo = Object.values(selectedTravellers || {}).map((traveller, index) => ({
      fname: traveller?.firstName || '',
      lname: traveller?.lastName || '',
      gender: traveller?.gender || '',
      age: traveller?.age || 0,
      title: traveller?.title || '',
      type: 'travellerInfo',
      is_primary: index === 0,
      reservation_type: traveller?.berthPreference || 'Full ticket (full berth)'
    }));

    const productList = [{
      id: `${railInfo?.train?.trainNumber}_${srcStn}_${destStn}`,
      train_number: railInfo?.train?.trainNumber || '',
      train_name: railInfo?.train?.trainName || '',
      total_fare: totalCollectibleAmount,
      from_station: {
        code: srcStn,
        name: srcCity,
        departure_time: railInfo?.train?.departureTime || '',
        departure_date: new Date(fromDateTime).toString()
      },
      to_station: {
        code: destStn,
        name: destCity,
        arrival_time: railInfo?.train?.arrivalTime || '',
        arrival_date: new Date(fromDateTime + ((railInfo?.train?.duration || 0) * 60000)).toString()
      },
      date_of_journey: doj,
      quota: railInfo?.selectedQuota || '',
      class: railInfo?.selectedClass || '',
      boarding_station_selected: (typeof railInfo?.train?.boardingStation === 'string' ? railInfo?.train?.boardingStation : railInfo?.train?.boardingStation?.stationCode) || srcStn,
      rank: 1,
      price: {
        total_amount: totalCollectibleAmount,
        base_fare_per_pax: railInfo?.availability?.totalFare || 0,
        reservation_charge_per_pax: railInfo?.availability?.reservationCharge || 0,
        free_cancellation: freeCancellationSelectedOption ? (freeCancellationResponse?.insuranceAmount || 0) : 0,
        dynamic_fare_per_pax: null,
        catering_charge_per_pax: null,
        super_fast_charge_per_pax: null,
        tax_per_pax: null,
        irctc_travel_insurance_premium: railInfo?.availability?.irctcTravelInsurancePremium || null,
        irctc_convinience_free: railInfo?.availability?.irctcConvinienceFee || null,
        agent_service_charge_and_gst: railInfo?.availability?.agentServiceChargeAndGst || null,
        discounts: {
          applicable_coupons: selectedCoupon?.couponCode ? [{
            code: selectedCoupon.couponCode,
            discount: Math.abs(discountAmount),
            is_applied: true
          }] : []
        }
      },
      legs: [{
        id: `${railInfo?.train?.trainNumber}_${srcStn}_${destStn}_${doj}`,
        name: railInfo?.train?.trainName || '',
        number: railInfo?.train?.trainNumber || '',
        product_type: railInfo?.selectedClass || '',
        duration: railInfo?.train?.duration || 0,
        sku_type: 'Regular Train',
        from_station: {
          code: srcStn,
          name: srcCity,
          departure_date_time: fromDateTime
        },
        to_station: {
          code: destStn,
          name: destCity,
          arrival_date_time: fromDateTime + ((railInfo?.train?.duration || 0) * 60000)
        },
        boarding_station: {
          code: (typeof railInfo?.train?.boardingStation === 'string' ? railInfo?.train?.boardingStation : railInfo?.train?.boardingStation?.stationCode) || srcStn,
          departure_date_time: fromDateTime
        },
        availability_shown: railInfo?.availability?.availablityStatus || '',
        fare_shown: totalCollectibleAmount,
        is_dynamic_pricing_applicable: false,
        rank: 1,
        catalog_list: [{
          quota: railInfo?.selectedQuota || '',
          class_name: railInfo?.selectedClass || '',
          price: {
            total_amount: totalCollectibleAmount
          },
          status: railInfo?.availability?.availablityStatus || '',
          availability_last_updated: 'Updated few mins ago',
          trip_guarantee_available: railofySelectedOption,
          source_of_data: 'landing'
        }]
      }]
    }];
    const bookingDate = new Date();
    const travelDate = new Date(fromDateTime);
    const advancePurchase = String(Math.floor((travelDate - bookingDate) / (1000 * 60 * 60 * 24)));

    const xdmPayload = {
      page_context: {
        page_type: 'funnel',
        page_name: 'review',
        prev_page_name: 'travellers',
        funnel_step: 'review',
        lob: 'rails',
        lob_category: 'rails',
        travel_store: {
          store_id: 'MMT'
        }
      },
      search_context: {
        ...buildSearchContext(
          { code: srcStn, city: srcCity },
          { code: destStn, city: destCity },
          'All'
        ),
        from_date_time: fromDateTime,
        advance_purchase: advancePurchase,
        pax: {
          count: travellerInfo.length,
          details: {
            adult: {
              count: travellerInfo.length
            },
            child: {
              count: 0,
              ages: []
            },
            infant: {
              count: 0
            }
          }
        }
      },
      event_detail: {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: 'button-clicked',
        event_type: 'action',
        event_value: EVENT_VALUES.REVIEW_CLICKED,
        interaction_type: 'button_click',
        booking_info: {
          from_date_time: fromDateTime,
          booking_id: '',
          price: totalCollectibleAmount,
          booking_date: new Date().toLocaleDateString('en-US'),
          destination: destCity,
          is_self_booking: false,
          currency: 'INR',
          tax: -1,
          applied_coupon: selectedCoupon?.couponCode || '',
          price_detail: {
            total_discount_amount: Math.abs(discountAmount),
            sell_amount: totalCollectibleAmount,
            cost_amount: totalCollectibleAmount,
            sp_amount: totalCollectibleAmount
          }
        },
        components: {
          id: 'review_page_component',
          product_list: productList,
          time_spent: 0
        }
      },
      addon_details: addonDetails,
      traveller_info: travellerInfo,
      event_tracking_context: eventTrackingContext()
    };
    
    await sendXDMEvent(xdmPayload, EVENT_NAMES.PAY_AND_BOOK, isXdmEnabled);
  } catch (xdmError) {
    console.error(`${EVENT_NAMES.PAY_AND_BOOK} Error:`, xdmError.message);
  }
};